import { useGetQuickbooksBillPushStatus } from "@/accounts-payable/query";
import {
  QUICKBOOKS_PUSH_LABEL,
  quickbooksPushMode,
  quickbooksPushNote,
} from "@/accounts-payable/quickbooks-bill";
import type { ApInvoice } from "@/accounts-payable/types";
import { useGetQuickbooksConnection } from "@/business/quickbooks-query";

/**
 * Where an invoice stands for a push to QuickBooks, shared by the push button
 * and its note so both read one answer (React Query dedupes the requests).
 * `ready` is false until both the connection and the push status are known,
 * and stays false for a business without QuickBooks bills, so nothing shows.
 */
export const useQuickbooksBillPush = (
  invoice: Pick<ApInvoice, "id" | "quickbooks_bill">,
  businessId: string | null,
) => {
  const { data: connection } = useGetQuickbooksConnection(businessId);
  // A disconnected business still shows it, with the API's reason.
  const hasQuickbooks =
    !!connection &&
    connection.status !== "not_connected" &&
    connection.bill_push_available;
  const status = useGetQuickbooksBillPushStatus(businessId, invoice.id, {
    enabled: hasQuickbooks,
  });

  const data = hasQuickbooks ? status.data : undefined;
  const mode = data ? quickbooksPushMode(data, invoice.quickbooks_bill) : null;

  return {
    ready: !!data && !!mode,
    isError: hasQuickbooks && status.isError,
    pushable: !!data?.pushable,
    mode,
    label: mode ? QUICKBOOKS_PUSH_LABEL[mode] : null,
    note: data ? quickbooksPushNote(data, invoice.quickbooks_bill) : null,
    /** The note's element id, which the disabled button points to. */
    noteId: `quickbooks-push-note-${invoice.id}`,
  };
};
